import { RoleLink } from "@/components/client-role";
import { OpeningsBadge } from "@/components/dashboard/role-status";
import { EmptyState } from "@/components/dashboard/state";
import { StatusPill } from "@/components/dashboard/status-pill";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { STALL_DAYS, daysSince, formatDate } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { SearchListRow, SearchStatus } from "@ow/shared";
import { ListFilter, Search, X } from "lucide-react";
import { useMemo, useState } from "react";

type SortKey = "recent" | "candidates" | "title";
type FlagKey = "stalled" | "at_offer";

const SORTS: { value: SortKey; label: string }[] = [
  { value: "recent", label: "Recently updated" },
  { value: "candidates", label: "Most applications" },
  { value: "title", label: "Role A–Z" },
];

const STATUS_ORDER: SearchStatus[] = ["active", "on_hold", "closed"];
const STATUS_LABEL: Record<SearchStatus, string> = { active: "Active", on_hold: "On hold", closed: "Closed" };

const FLAGS: { key: FlagKey; label: string }[] = [
  { key: "stalled", label: "Stalled" },
  { key: "at_offer", label: "At offer" },
];

// One column template shared by the header row and every data row so columns line up straight down the
// list. Alignment follows one deliberate rule end-to-end: the identity column hugs the LEFT gutter, the
// meta column (Updated) hugs the RIGHT gutter, and the interior columns are all left-aligned — so the
// row's left and right gutters are symmetric and no single column is aligned arbitrarily. Applied
// identically on all three tiers (C-Suite / VP-Director / General) so the pages are structurally the same.
const GRID = "grid grid-cols-[minmax(0,1fr)_120px_104px_112px] items-center gap-6";

/** The one role list shared by every tier page. Each row is a role, so we surface its live application
 *  count — not a single "furthest stage", which would misrepresent a role whose applicants sit at
 *  different stages. The toolbar is the same everywhere — search, Function + Status inline, a "Filters"
 *  popover for the rest (owner, needs-attention, has-applicants), then sort. Stalled active roles flag
 *  amber. This is a single-client portal, so there is deliberately NO cross-firm dimension. */
export function SearchList({
  rows,
  loading,
  emptyLabel = "No active roles in this tier yet.",
}: {
  rows: SearchListRow[];
  loading?: boolean;
  emptyLabel?: string;
}) {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("recent");
  const [status, setStatus] = useState<SearchStatus | "all">("all");
  const [fn, setFn] = useState<string>("all");
  const [owner, setOwner] = useState<string>("all");
  const [flags, setFlags] = useState<Set<FlagKey>>(() => new Set());
  const [hasApplicants, setHasApplicants] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  // Option lists come from the rows actually present, so a dropdown never offers a value with no
  // matches — but each control is always rendered, so the toolbar keeps the same shape on every tier.
  const functions = useMemo(() => uniqueSorted(rows.map((r) => r.function)), [rows]);
  const owners = useMemo(() => uniqueSorted(rows.map((r) => r.ownerName)), [rows]);
  const statusesPresent = useMemo(() => {
    const set = new Set(rows.map((r) => r.status));
    return STATUS_ORDER.filter((s) => set.has(s));
  }, [rows]);

  // Guard each control against a value that isn't present in this tier's data (e.g. switching tiers).
  const activeStatus = status !== "all" && statusesPresent.includes(status) ? status : "all";
  const activeFn = fn !== "all" && functions.includes(fn) ? fn : "all";
  const activeOwner = owner !== "all" && owners.includes(owner) ? owner : "all";

  // Count of the "advanced" filters tucked in the popover, shown as a badge on the trigger.
  const moreCount = (activeOwner !== "all" ? 1 : 0) + flags.size + (hasApplicants ? 1 : 0);
  const anyFilter = query.trim().length > 0 || activeStatus !== "all" || activeFn !== "all" || moreCount > 0;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const out = rows.filter((r) => {
      if (activeStatus !== "all" && r.status !== activeStatus) return false;
      if (activeFn !== "all" && r.function !== activeFn) return false;
      if (activeOwner !== "all" && r.ownerName !== activeOwner) return false;
      if (hasApplicants && r.activeCandidates === 0) return false;
      if (flags.size > 0) {
        const isStalled = r.status === "active" && daysSince(r.updatedAt) > STALL_DAYS;
        const isAtOffer = r.status === "active" && r.atOffer;
        if (flags.has("stalled") && flags.has("at_offer")) {
          if (!isStalled && !isAtOffer) return false;
        } else if (flags.has("stalled") && !isStalled) {
          return false;
        } else if (flags.has("at_offer") && !isAtOffer) {
          return false;
        }
      }
      if (q) {
        const hay = `${r.title} ${r.function ?? ""} ${r.ownerName ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return out.sort((a, b) => {
      switch (sort) {
        case "candidates":
          return b.activeCandidates - a.activeCandidates;
        case "title":
          return a.title.localeCompare(b.title);
        default:
          return (b.updatedAt ?? "").localeCompare(a.updatedAt ?? "");
      }
    });
  }, [rows, activeStatus, activeFn, activeOwner, flags, hasApplicants, query, sort]);

  const toggleFlag = (key: FlagKey) =>
    setFlags((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const clearAll = () => {
    setQuery("");
    setStatus("all");
    setFn("all");
    setOwner("all");
    setFlags(new Set());
    setHasApplicants(false);
  };

  if (loading) {
    return (
      <div className="rounded-xl border bg-card px-3 py-10 text-center text-sm text-muted-foreground">Loading…</div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Toolbar — identical on every tier. Search + the two most-used filters (Function, Status) sit
          inline; the rest fold into a "Filters" popover so the row never crowds. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search roles, functions, leads…"
            aria-label="Search roles, functions, or leads"
            className="h-9 w-full rounded-md border bg-card pl-9 pr-8 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
          />
          {query.trim().length > 0 ? (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear search"
              className="absolute right-2 top-1/2 grid size-5 -translate-y-1/2 place-items-center rounded text-muted-foreground outline-none hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </div>

        <Select value={activeFn} onValueChange={setFn}>
          <SelectTrigger className="h-9 w-[150px] bg-card" aria-label="Filter by function">
            <SelectValue placeholder="Function" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All functions</SelectItem>
            {functions.map((f) => (
              <SelectItem key={f} value={f}>
                {f}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={activeStatus} onValueChange={(v) => setStatus(v as SearchStatus | "all")}>
          <SelectTrigger className="h-9 w-[140px] bg-card" aria-label="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statusesPresent.map((s) => (
              <SelectItem key={s} value={s}>
                {STATUS_LABEL[s]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <MoreFilters
          open={moreOpen}
          onOpenChange={setMoreOpen}
          count={moreCount}
          owner={activeOwner}
          owners={owners}
          onOwner={setOwner}
          flags={flags}
          onToggleFlag={toggleFlag}
          hasApplicants={hasApplicants}
          onHasApplicants={setHasApplicants}
        />

        <Select value={sort} onValueChange={(v) => setSort(v as SortKey)}>
          <SelectTrigger className="h-9 w-[172px] bg-card" aria-label="Sort roles">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORTS.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Result count appears only while filtering — a plain "N roles" chip in the toolbar was noise
          when the rows are right there to be counted. Filtered, "showing X of Y" is genuinely useful. */}
      {anyFilter ? (
        <div className="flex items-center gap-2 px-0.5 text-xs text-muted-foreground" aria-live="polite">
          <span className="tabular-nums">
            Showing {visible.length} of {rows.length} roles
          </span>
          <span aria-hidden>·</span>
          <button
            type="button"
            onClick={clearAll}
            className="font-medium text-foreground underline-offset-2 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
          >
            Clear filters
          </button>
        </div>
      ) : null}

      {visible.length === 0 ? (
        <EmptyState icon={Search}>{anyFilter ? "No roles match your filters." : emptyLabel}</EmptyState>
      ) : (
        <div className="overflow-hidden rounded-xl border bg-card">
          <div className={cn(GRID, "border-b bg-muted/30 px-5 py-2.5")}>
            <span className="eyebrow">Role</span>
            <span className="eyebrow">Applications</span>
            <span className="eyebrow">Status</span>
            <span className="eyebrow text-right">Updated</span>
          </div>
          <div className="divide-y">
            {visible.map((r) => {
              const stalled = r.status === "active" && daysSince(r.updatedAt) > STALL_DAYS;
              const inactive = r.status !== "active";
              return (
                <RoleLink
                  key={r.id}
                  to="/search/$searchId"
                  params={{ searchId: r.id }}
                  className={cn(
                    GRID,
                    "px-5 py-3.5 outline-none transition-colors hover:bg-accent/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring",
                  )}
                >
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-2">
                      <span
                        className={cn(
                          "truncate text-sm font-medium leading-tight",
                          inactive && "text-muted-foreground",
                        )}
                      >
                        {r.title}
                      </span>
                      <OpeningsBadge openings={r.openings} />
                    </div>
                    {r.function ? <div className="truncate text-xs text-muted-foreground">{r.function}</div> : null}
                  </div>

                  <div
                    className={cn("text-sm tabular-nums", r.activeCandidates === 0 && "text-muted-foreground")}
                    title={`${r.activeCandidates} active application${r.activeCandidates === 1 ? "" : "s"}`}
                  >
                    {r.activeCandidates}
                  </div>

                  <div>
                    <StatusPill value={r.status} />
                  </div>

                  <span
                    className={cn(
                      "flex items-center justify-end gap-1.5 text-xs tabular-nums",
                      stalled ? "text-[var(--color-warning)]" : "text-muted-foreground",
                    )}
                    title={stalled ? `Stalled — no activity in ${daysSince(r.updatedAt)} days` : undefined}
                  >
                    {stalled ? <span className="size-1.5 shrink-0 rounded-full bg-[var(--color-warning)]" /> : null}
                    {formatDate(r.updatedAt)}
                  </span>
                </RoleLink>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/** Unique non-null values, case-insensitively sorted — for the function/owner option lists. */
function uniqueSorted(values: (string | null)[]): string[] {
  const set = new Set<string>();
  for (const v of values) if (v) set.add(v);
  return [...set].sort((a, b) => a.localeCompare(b));
}

/** The "Filters" popover — the less-used filters kept off the toolbar row so it never crowds: owner
 *  (the lead consultant), needs-attention flags, and a has-applicants toggle. A badge on the trigger
 *  shows how many are active. Same lightweight overlay pattern used elsewhere in the app. */
function MoreFilters({
  open,
  onOpenChange,
  count,
  owner,
  owners,
  onOwner,
  flags,
  onToggleFlag,
  hasApplicants,
  onHasApplicants,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  count: number;
  owner: string;
  owners: string[];
  onOwner: (v: string) => void;
  flags: Set<FlagKey>;
  onToggleFlag: (k: FlagKey) => void;
  hasApplicants: boolean;
  onHasApplicants: (v: boolean) => void;
}) {
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => onOpenChange(!open)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={cn(
          "inline-flex h-9 items-center gap-1.5 rounded-md border bg-card px-3 text-sm outline-none transition-colors hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring",
          count > 0 ? "text-foreground" : "text-muted-foreground",
        )}
      >
        <ListFilter className="size-4" />
        Filters
        {count > 0 ? (
          <span className="grid min-w-4 place-items-center rounded-full bg-foreground/15 px-1 text-[11px] leading-none tabular-nums text-foreground">
            {count}
          </span>
        ) : null}
      </button>
      {open ? (
        <>
          <button
            type="button"
            aria-label="Close filters"
            className="fixed inset-0 z-10 cursor-default"
            onClick={() => onOpenChange(false)}
          />
          <div className="absolute right-0 z-20 mt-1 w-72 space-y-4 rounded-lg border bg-popover p-4 text-popover-foreground shadow-md">
            <label className="block space-y-1.5">
              <span className="eyebrow">Search lead</span>
              <Select value={owner} onValueChange={onOwner}>
                <SelectTrigger className="h-9 w-full bg-card">
                  <SelectValue placeholder="All leads" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All leads</SelectItem>
                  {owners.map((o) => (
                    <SelectItem key={o} value={o}>
                      {o}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>

            <div className="space-y-2">
              <span className="eyebrow">Needs attention</span>
              <div className="flex flex-wrap gap-1.5">
                {FLAGS.map((f) => {
                  const on = flags.has(f.key);
                  return (
                    <button
                      key={f.key}
                      type="button"
                      onClick={() => onToggleFlag(f.key)}
                      aria-pressed={on}
                      className={cn(
                        "rounded-full border px-2.5 py-1 text-xs font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                        on
                          ? "border-foreground/20 bg-accent text-foreground"
                          : "border-border bg-card text-muted-foreground hover:bg-accent hover:text-foreground",
                      )}
                    >
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <label className="flex cursor-pointer items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={hasApplicants}
                onChange={(e) => onHasApplicants(e.target.checked)}
                className="size-3.5 accent-[var(--brand-accent)]"
              />
              Only roles with applicants
            </label>
          </div>
        </>
      ) : null}
    </div>
  );
}
